import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ACTS = [
  { id: 0, eyebrow: 'Scroll to begin', title: 'The Mind', em: 'of Music', sub: 'An intelligence born from sound' },
  { id: 1, eyebrow: 'Aarohan Intelligence — Act I', title: 'Every note', em: 'has a soul', sub: 'We trained on 10 million ragas, compositions and progressions' },
  { id: 2, eyebrow: 'Aarohan Intelligence — Act II', title: 'It listens.', em: 'It learns.', sub: 'Real-time feedback. Pitch correction. Raga identification. Mood mapping.' },
  { id: 3, eyebrow: 'Aarohan Intelligence — Act III', features: true },
  { id: 4, eyebrow: 'Aarohan Intelligence', title: 'The Future', em: 'of Sound.', pill: 'Coming Soon' },
];

const FEATURES = [
  { n: '01', title: 'Riyaaz Coach', desc: 'AI pitch & rhythm feedback in real time' },
  { n: '02', title: 'Raga Mind', desc: 'Identifies raga from any hummed phrase' },
  { n: '03', title: 'Lyric Forge', desc: 'Co-write lyrics in Hindi or English' },
  { n: '04', title: 'Progress DNA', desc: 'Radial map of your musical genome' },
];

/* ─── Text block styles ─────────────────────────────────────────────────────
   The "3D lifted" illusion is achieved with:
   1. A translucent dark background behind the text (acts as a physical card)
   2. A layered box-shadow that simulates depth + ambient glow
   3. transform: translateZ(0) + will-change so the browser composites the
      text layer independently — particles on the WebGL canvas (z-index 0)
      can never bleed through a composited layer above it.
   4. perspective on the parent gives the card a real 3D tilt feel on hover
      (optional but adds the "3D" character you want).
────────────────────────────────────────────────────────────────────────────── */

const textCardStyle = {
  position: 'relative',
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '44px 56px 48px',
  borderRadius: 4,
  // Dark glass backing — particles visually sit BEHIND this surface
  background: 'rgba(5, 3, 1, 0.72)',
  backdropFilter: 'blur(0px)',           // no blur so it's crisp not foggy
  WebkitBackdropFilter: 'blur(0px)',
  border: '1px solid rgba(200,146,42,0.10)',
  // Multi-layer shadow: close dark shadow (depth) + far gold glow (lift)
  boxShadow: [
    '0 2px 0px rgba(200,146,42,0.25)',   // thin gold rim on bottom — "edge lit"
    '0 8px 32px rgba(0,0,0,0.85)',        // close dark shadow — depth
    '0 24px 80px rgba(0,0,0,0.65)',       // ambient dark shadow
    '0 0 120px rgba(200,146,42,0.06)',    // faint gold halo
  ].join(', '),
  // Forces its own compositor layer — WebGL canvas stays below
  transform: 'translateZ(0)',
  willChange: 'transform, opacity',
  isolation: 'isolate',
};

// Thin gold top-edge line — gives the "screen of a device" edge-lit feel
const cardEdgeStyle = {
  position: 'absolute',
  top: 0, left: '12%', right: '12%',
  height: '1px',
  background: 'linear-gradient(90deg, transparent, rgba(200,146,42,0.55) 40%, rgba(200,146,42,0.55) 60%, transparent)',
  borderRadius: 1,
};

const eyebrowStyle = {
  fontSize: 18, letterSpacing: "5px", textTransform: "uppercase",
  color: '#C8922A', marginBottom: 20,
  fontFamily: "'DM Sans',sans-serif",
  // Tiny gold text-shadow keeps it punchy over any bg
  textShadow: '0 0 24px rgba(200,146,42,0.6)',
};

const headlineStyle = {
  fontFamily: "'Cormorant Garamond',serif",
  fontSize: 'clamp(28px,4.5vw,64px)',
  fontWeight: 300,
  lineHeight: 0.9,
  color: '#FAF7F0',
  letterSpacing: '-2px',
  margin: '0 0 20px',
  // Strong text shadow so each letter has a raised, lit quality
  textShadow: '0 1px 0 rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.9)',
};

const subStyle = {
  fontSize: 13, letterSpacing: '2px',
  color: 'rgba(250,247,240,0.55)',
  textTransform: 'uppercase', maxWidth: 400,
  lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif",
  textShadow: '0 1px 8px rgba(0,0,0,0.8)',
};

const pillStyle = {
  marginTop: 32,
  border: '1px solid rgba(200,146,42,0.5)',
  borderRadius: 60,
  padding: '12px 40px',
  fontSize: 9, letterSpacing: '6px',
  textTransform: 'uppercase',
  color: '#C8922A',
  background: 'rgba(0,0,0,0.5)',
  fontFamily: "'DM Sans',sans-serif",
  textShadow: '0 0 12px rgba(200,146,42,0.4)',
};

const featCardStyle = {
  background: 'rgba(5,3,1,0.85)',
  padding: '28px 32px',
  textAlign: 'left',
};

export default function AIFeatures() {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const actsRef = useRef([]);

  useEffect(() => {
    const section = sectionRef.current;
    const canvas = canvasRef.current;
    if (!canvas || !section) return;

    const getW = () => canvas.parentElement.clientWidth;
    const getH = () => canvas.parentElement.clientHeight;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    renderer.setClearColor(0x000000, 1);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(getW(), getH());
    renderer.toneMapping = THREE.ACESFilmicToneMapping;

    const scene = new THREE.Scene();
    const cam = new THREE.PerspectiveCamera(60, getW() / getH(), 0.01, 500);
    cam.position.set(0, 0, 18);

    const N = 5000;
    const pos = new Float32Array(N * 3);
    const col = new Float32Array(N * 3);
    const sc = new Float32Array(N);
    const ph = new Float32Array(N);
    const galaxy = new Float32Array(N * 3);
    const tunnel = new Float32Array(N * 3);
    const burst = new Float32Array(N * 3);
    const sph = new Float32Array(N * 3);
    const grid = new Float32Array(N * 3);
    const gold = [[1, .71, .16], [.78, .57, .16], [.96, .85, .47], [1, .97, .87], [.55, .38, .08]];

    for (let i = 0; i < N; i++) {
      const i3 = i * 3;
      const arm = Math.floor(Math.random() * 3);
      const ao = (arm / 3) * Math.PI * 2;
      const r = 1 + Math.random() * 12;
      const th = ao + r * .4 + (Math.random() - .5) * .9;
      galaxy[i3] = Math.cos(th) * r + (Math.random() - .5) * 1.5;
      galaxy[i3 + 1] = (Math.random() - .5) * 2 * (1 / (1 + r * .08));
      galaxy[i3 + 2] = Math.sin(th) * r + (Math.random() - .5) * 1.5;
      const ta = Math.random() * Math.PI * 2, tz = (Math.random() - .5) * 40, tr = 1 + Math.random() * 3;
      tunnel[i3] = Math.cos(ta) * tr + (Math.random() - .5) * .5;
      tunnel[i3 + 1] = Math.sin(ta) * tr + (Math.random() - .5) * .5;
      tunnel[i3 + 2] = tz;
      const ba = Math.random() * Math.PI * 2, br = Math.random() * 16;
      burst[i3] = Math.cos(ba) * br; burst[i3 + 1] = (Math.random() - .5) * 8; burst[i3 + 2] = Math.sin(ba) * br;
      const sp = Math.acos(2 * Math.random() - 1), st = Math.random() * Math.PI * 2, sr = 2 + Math.random() * 1.5;
      sph[i3] = Math.sin(sp) * Math.cos(st) * sr; sph[i3 + 1] = Math.cos(sp) * sr; sph[i3 + 2] = Math.sin(sp) * Math.sin(st) * sr;
      const gi = i % 50, gj = Math.floor(i / 50) % 100;
      grid[i3] = (gi - 25) * 1.2 + (Math.random() - .5) * .3;
      grid[i3 + 1] = (gj - 50) * .5 + (Math.random() - .5) * .3;
      grid[i3 + 2] = (Math.random() - .5) * 2;
      pos[i3] = galaxy[i3]; pos[i3 + 1] = galaxy[i3 + 1]; pos[i3 + 2] = galaxy[i3 + 2];
      const c = gold[Math.floor(Math.random() * gold.length)];
      col[i3] = c[0]; col[i3 + 1] = c[1]; col[i3 + 2] = c[2];
      sc[i] = .3 + Math.random() * 2; ph[i] = Math.random() * Math.PI * 2;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.setAttribute('aScale', new THREE.BufferAttribute(sc, 1));
    geo.setAttribute('aPhase', new THREE.BufferAttribute(ph, 1));

    const mat = new THREE.ShaderMaterial({
      vertexColors: true, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uTime: { value: 0 }, uSize: { value: 55 * renderer.getPixelRatio() }, uBrightness: { value: 1 } },
      vertexShader: `
        attribute float aScale; attribute float aPhase;
        uniform float uTime; uniform float uSize; uniform float uBrightness;
        varying vec3 vColor; varying float vAlpha;
        void main(){
          vColor=color;
          float pulse=.7+.3*sin(uTime*1.5+aPhase);
          vAlpha=pulse*uBrightness;
          vec4 mv=modelViewMatrix*vec4(position,1.);
          gl_PointSize=uSize*aScale/(-mv.z);
          gl_Position=projectionMatrix*mv;
        }
      `,
      fragmentShader: `
        varying vec3 vColor; varying float vAlpha;
        void main(){
          float d=length(gl_PointCoord-.5);
          if(d>.5)discard;
          float a=smoothstep(.5,.0,d)*vAlpha;
          gl_FragColor=vec4(vColor,a);
        }
      `
    });

    const pts = new THREE.Points(geo, mat);
    scene.add(pts);

    const glowMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      uniforms: { uTime: { value: 0 }, uVis: { value: 0 } },
      vertexShader: `varying vec3 vN; void main(){vN=normalize(normalMatrix*normal);gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}`,
      fragmentShader: `varying vec3 vN; uniform float uTime; uniform float uVis; void main(){float rim=1.-dot(vN,vec3(0,0,1));float p=.5+.5*sin(uTime*4.);float a=uVis*rim*rim*2.*p;gl_FragColor=vec4(.78,.57,.16,a);}`
    });
    const glowSphere = new THREE.Mesh(new THREE.SphereGeometry(.5, 32, 32), glowMat);
    scene.add(glowSphere);

    const ringMat = new THREE.MeshBasicMaterial({ color: 0xC8922A, transparent: true, opacity: 0 });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1, .02, 16, 128), ringMat);
    ring.rotation.x = Math.PI / 2;
    scene.add(ring);

    const posAttr = geo.attributes.position;
    const state = { camZ: 18, camX: 0, camY: 0, rotY: 0, rotX: 0, rotZ: 0, brightness: 1, glowVis: 0, ringOpacity: 0, ringScale: 1 };

    let time = 0, rafId;
    const animate = () => {
      rafId = requestAnimationFrame(animate);
      time += .008;
      cam.position.x += (state.camX - cam.position.x) * .06;
      cam.position.y += (state.camY - cam.position.y) * .06;
      cam.position.z += (state.camZ - cam.position.z) * .05;
      cam.lookAt(0, 0, 0);
      pts.rotation.y += (state.rotY - pts.rotation.y) * .03;
      pts.rotation.x += (state.rotX - pts.rotation.x) * .03;
      mat.uniforms.uTime.value = time;
      mat.uniforms.uBrightness.value = state.brightness;
      glowMat.uniforms.uTime.value = time;
      glowMat.uniforms.uVis.value += (state.glowVis - glowMat.uniforms.uVis.value) * .05;
      ringMat.opacity += (state.ringOpacity - ringMat.opacity) * .05;
      ring.scale.x += (state.ringScale - ring.scale.x) * .05;
      ring.scale.y += (state.ringScale - ring.scale.y) * .05;
      ring.rotation.z = time * .6;
      ring.rotation.x = Math.PI / 2 + Math.sin(time * .4) * .15;
      renderer.render(scene, cam);
    };
    animate();

    const sources = [galaxy, tunnel, burst, sph, grid];

    const scrollTriggerInstance = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 1,
      onUpdate(self) {
        const p = self.progress;
        const si = Math.min(Math.floor(p * 4), 3);
        const lt = p * 4 - si;
        const e = lt < .5 ? 2 * lt * lt : 1 - Math.pow(-2 * lt + 2, 2) / 2;
        const from = sources[si], to = sources[si + 1];
        for (let i = 0; i < N; i++) {
          const i3 = i * 3;
          posAttr.array[i3] = from[i3] * (1 - e) + to[i3] * e;
          posAttr.array[i3 + 1] = from[i3 + 1] * (1 - e) + to[i3 + 1] * e;
          posAttr.array[i3 + 2] = from[i3 + 2] * (1 - e) + to[i3 + 2] * e;
        }
        posAttr.needsUpdate = true;

        // Update progress spine fill
        const fill = document.getElementById('ai-progress-fill');
        if (fill) fill.style.height = `${p * 100}%`;

        const act = Math.min(Math.floor(p * 5), 4);
        actsRef.current.forEach((el, i) => {
          if (!el) return;
          const lp = p * 5 - i;
          let op = 0;
          if (Math.abs(act - i) === 0) {
            if (lp < .15) op = lp / .15;
            else if (lp > .85) op = 1 - (lp - .85) / .15;
            else op = 1;
          }
          el.style.opacity = op;
          el.style.transform = `translateY(${(lp - .5) * 40}px) scale(${.94 + op * .06})`;
        });

        if (act === 0) { state.camZ = 18; state.camX = 0; state.camY = 0; state.rotY = 0; state.rotX = 0; state.brightness = 1; state.glowVis = 0; state.ringOpacity = 0; state.ringScale = 1; }
        else if (act === 1) { const lp = p * 5 - 1; state.camZ = 14 - lp * 2; state.camX = Math.sin(lp * 2) * 2; state.rotY = lp * .8; state.rotX = lp * .1; state.glowVis = .3 * lp; state.ringOpacity = .3 * lp; state.ringScale = 1 + lp * .5; }
        else if (act === 2) { const lp = p * 5 - 2; state.camZ = 8 - lp * 3; state.rotY = 2 + lp * 1.5; state.rotX = .2 + lp * .2; state.brightness = .8 + lp * .4; state.glowVis = .5 + lp * .5; state.ringOpacity = .6; state.ringScale = 1.5 + lp; }
        else if (act === 3) { const lp = p * 5 - 3; state.camZ = 6 + lp * 2; state.camX = Math.cos(lp * 3) * 1.5; state.camY = Math.sin(lp * 3) * 1; state.rotY = 3.5 + lp * .4; state.brightness = 1.2; state.glowVis = .8; state.ringOpacity = .8 + lp * .2; state.ringScale = 2.5 + lp * 2; }
        else if (act === 4) { state.camZ = 4; state.camX = 0; state.camY = 0; state.brightness = 1.5; state.glowVis = 1; state.ringOpacity = 1; state.ringScale = 5; }
      }
    });

    const onResize = () => {
      renderer.setSize(getW(), getH());
      cam.aspect = getW() / getH();
      cam.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      scrollTriggerInstance.kill();
      renderer.dispose();
    };
  }, []);

  return (
    <section ref={sectionRef} id="ai" style={{ height: '700vh', position: 'relative', background: '#000' }}>

      {/* ── Sticky viewport ─────────────────────────────────────────────── */}
      <div style={{ position: 'sticky', top: 0, height: '100vh', width: '100%', overflow: 'hidden', background: '#000' }}>

        {/* Layer 0 — WebGL canvas, sits at the very back */}
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0 }} />

        {/* Layer 1 — waveform decoration, also behind text */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, display: 'flex', alignItems: 'flex-end', gap: 2, padding: '0 10%', opacity: .25, zIndex: 1 }}>
          {Array.from({ length: 48 }, (_, i) => (
            <div key={i} style={{
              width: 3, background: '#C8922A', borderRadius: '2px 2px 0 0',
              height: `${20 + Math.sin(i * .4) * 14 + Math.random() * 14}px`,
              animation: `waveAnim ${0.6 + (i % 7) * .15}s ease-in-out infinite alternate`,
              animationDelay: `${i * .04}s`, transformOrigin: 'bottom',
            }} />
          ))}
        </div>

        {/* Layer 2 — progress spine */}
        <div style={{ position: 'absolute', left: 32, top: '50%', transform: 'translateY(-50%)', height: 200, width: 1, background: 'rgba(200,146,42,.15)', zIndex: 2 }}>
          <div style={{ width: '100%', background: '#C8922A' }} id="ai-progress-fill" />
        </div>

        {/* Layer 3 — act text blocks
            Each act wraps its content in the "3D card" which uses:
            - isolation: isolate  → new stacking context
            - transform: translateZ(0) → own compositor layer
            Particles rendered by WebGL (z-index 0) can NEVER pierce through. */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3, perspective: '1200px' }}>
          {ACTS.map((act, i) => (
            <div key={i} ref={el => actsRef.current[i] = el}
              style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                opacity: i === 0 ? 1 : 0, transition: 'none',
              }}
            >
              {/* ── The 3D card that blocks particles ── */}
              <div style={textCardStyle}>
                {/* Edge-lit top line */}
                <div style={cardEdgeStyle} />

                <span style={eyebrowStyle}>{act.eyebrow}</span>

                {!act.features ? (
                  <>
                    <h2 style={headlineStyle}>
                      {act.title}<br />
                      <em style={{ color: '#C8922A', fontStyle: 'italic' }}>{act.em}</em>
                    </h2>
                    {act.sub && <p style={subStyle}>{act.sub}</p>}
                    {act.pill && <div style={pillStyle}>{act.pill}</div>}
                  </>
                ) : (
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1,
                    background: 'rgba(200,146,42,.15)',
                    border: '1px solid rgba(200,146,42,.15)',
                    maxWidth: 600, width: '90%',
                  }}>
                    {FEATURES.map(f => (
                      <div key={f.n} style={featCardStyle}>
                        <div style={{ fontSize: 9, letterSpacing: '4px', color: '#C8922A', marginBottom: 10, fontFamily: "'DM Sans',sans-serif" }}>{f.n}</div>
                        <div style={{
                          fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: '#FAF7F0', fontWeight: 300, marginBottom: 6,
                          textShadow: '0 2px 12px rgba(0,0,0,0.9)'
                        }}>{f.title}</div>
                        <div style={{ fontSize: 12, color: 'rgba(250,247,240,.5)', lineHeight: 1.7, fontFamily: "'DM Sans',sans-serif" }}>{f.desc}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
